/**
 * Utilitário para aplicar cores do tema dinamicamente
 *
 * Este arquivo lê as cores das variáveis de ambiente através do Config
 * e aplica como CSS custom properties no :root do documento.
 * Isso permite que as cores sejam configuráveis via .env
 */

import { Config } from '../services/config';

/**
 * Função auxiliar para converter cor hexadecimal para RGB
 */
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Função auxiliar para converter RGB para hexadecimal
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Função para clarear uma cor hexadecimal
 */
const lighten = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * amount));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * amount));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * amount));

  return rgbToHex(r, g, b);
};

/**
 * Função para escurecer uma cor hexadecimal
 */
const darken = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const r = Math.max(0, Math.round(rgb.r * (1 - amount)));
  const g = Math.max(0, Math.round(rgb.g * (1 - amount)));
  const b = Math.max(0, Math.round(rgb.b * (1 - amount)));

  return rgbToHex(r, g, b);
};

/**
 * Aplica as cores do tema como variáveis CSS no :root
 * Deve ser chamado na inicialização da aplicação
 */
export const applyThemeColors = (): void => {
  const root = document.documentElement;

  // Cores principais (configuráveis via .env)
  const PRIMARY_COLOR = Config.THEME.PRIMARY_COLOR;      // #04364F (Azul escuro)
  const SECONDARY_COLOR = Config.THEME.SECONDARY_COLOR;  // #04C64F (Verde)
  const DANGER_COLOR = Config.THEME.DANGER_COLOR;        // #E74C3C
  const SUCCESS_COLOR = Config.THEME.SUCCESS_COLOR;      // #27AE60
  const WARNING_COLOR = Config.THEME.WARNING_COLOR;      // #F39C12
  const INFO_COLOR = Config.THEME.INFO_COLOR;            // #3498DB

  // Gerar variações da cor primária
  const PRIMARY_LIGHT = lighten(PRIMARY_COLOR, 0.15);    // ~15% mais claro
  const PRIMARY_HOVER = lighten(PRIMARY_COLOR, 0.10);    // ~10% mais claro
  const PRIMARY_DARK = darken(PRIMARY_COLOR, 0.10);      // ~10% mais escuro
  const PRIMARY_DARKER = darken(PRIMARY_COLOR, 0.20);    // ~20% mais escuro

  // Gerar variações da cor secundária
  const SECONDARY_LIGHT = lighten(SECONDARY_COLOR, 0.20);
  const SECONDARY_HOVER = lighten(SECONDARY_COLOR, 0.10);
  const SECONDARY_DARK = darken(SECONDARY_COLOR, 0.10);
  const SECONDARY_DARKER = darken(SECONDARY_COLOR, 0.20);

  // Aplicar cores da marca
  root.style.setProperty('--color-primary', PRIMARY_COLOR);
  root.style.setProperty('--color-primary-light', PRIMARY_LIGHT);
  root.style.setProperty('--color-primary-hover', PRIMARY_HOVER);
  root.style.setProperty('--color-primary-dark', PRIMARY_DARK);
  root.style.setProperty('--color-primary-darker', PRIMARY_DARKER);

  root.style.setProperty('--color-secondary', SECONDARY_COLOR);
  root.style.setProperty('--color-secondary-light', SECONDARY_LIGHT);
  root.style.setProperty('--color-secondary-hover', SECONDARY_HOVER);
  root.style.setProperty('--color-secondary-dark', SECONDARY_DARK);
  root.style.setProperty('--color-secondary-darker', SECONDARY_DARKER);

  // Cores de status
  root.style.setProperty('--color-success', SUCCESS_COLOR);
  root.style.setProperty('--color-warning', WARNING_COLOR);
  root.style.setProperty('--color-danger', DANGER_COLOR);
  root.style.setProperty('--color-info', INFO_COLOR);

  // Cores de fundo
  root.style.setProperty('--bg-primary', PRIMARY_COLOR);
  root.style.setProperty('--bg-dark', PRIMARY_COLOR);
  root.style.setProperty('--bg-darker', PRIMARY_DARKER);

  // Cores de texto
  root.style.setProperty('--text-primary', PRIMARY_COLOR);

  // Cores de borda
  root.style.setProperty('--border-primary', PRIMARY_COLOR);
  root.style.setProperty('--border-secondary', SECONDARY_COLOR);

  // Cores de botões
  root.style.setProperty('--btn-primary', PRIMARY_COLOR);
  root.style.setProperty('--btn-primary-hover', PRIMARY_HOVER);
  root.style.setProperty('--btn-secondary', SECONDARY_COLOR);
  root.style.setProperty('--btn-secondary-hover', SECONDARY_HOVER);
  root.style.setProperty('--btn-edit', SECONDARY_COLOR);
  root.style.setProperty('--btn-edit-hover', SECONDARY_HOVER);
  root.style.setProperty('--btn-view', INFO_COLOR);
  root.style.setProperty('--btn-delete', DANGER_COLOR);

  // Gradientes baseados na cor primária
  root.style.setProperty('--gradient-primary', `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_HOVER} 100%)`);
  root.style.setProperty('--gradient-primary-alt', `linear-gradient(45deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_LIGHT} 100%)`);
  root.style.setProperty('--gradient-secondary', `linear-gradient(135deg, ${SECONDARY_COLOR} 0%, ${SECONDARY_HOVER} 100%)`);
  root.style.setProperty('--gradient-secondary-alt', `linear-gradient(45deg, ${SECONDARY_COLOR} 0%, ${SECONDARY_LIGHT} 100%)`);
  root.style.setProperty('--gradient-brand-mix', `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`);
  root.style.setProperty('--gradient-sidebar', `linear-gradient(180deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_DARKER} 100%)`);
  root.style.setProperty('--gradient-header', `linear-gradient(90deg, ${PRIMARY_COLOR} 0%, ${PRIMARY_HOVER} 100%)`);

  // Sombras baseadas na cor primária
  const primaryRgb = hexToRgb(PRIMARY_COLOR);
  if (primaryRgb) {
    root.style.setProperty('--shadow-primary', `0 4px 15px rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.3)`);
    root.style.setProperty('--shadow-info', `0 4px 15px rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.3)`);
  }

  const secondaryRgb = hexToRgb(SECONDARY_COLOR);
  if (secondaryRgb) {
    root.style.setProperty('--shadow-secondary', `0 4px 15px rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.3)`);
  }

  // Opacidades baseadas na cor primária
  if (primaryRgb) {
    root.style.setProperty('--opacity-hover-primary', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
    root.style.setProperty('--opacity-hover-primary-strong', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.2)`);
    root.style.setProperty('--opacity-overlay', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.5)`);
    root.style.setProperty('--opacity-overlay-dark', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.8)`);
    root.style.setProperty('--opacity-divider', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
  }

  if (secondaryRgb) {
    root.style.setProperty('--opacity-hover-secondary', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.1)`);
    root.style.setProperty('--opacity-hover-secondary-strong', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.2)`);
  }

};

/**
 * Hook para aplicar tema ao montar o componente
 * Uso: useEffect(() => { applyThemeColors(); }, []);
 */
export default applyThemeColors;
